<template>
  <div class="duplicate-keywords-modal">
    <div class="duplicate-keywords-header">
      <h4 v-text="$t('duplicate-keywords')" />
      <closer @click="close" />
    </div>
    <div class="duplicate-keywords-body">
      <div>
        <p class="duplicate-keywords-description" v-text="$t('duplicate-keywords-description')" />
        <div class="search-project-field">
          <custom-v-select
            id="project"
            :class="customSelectClasses"
            :options="getParsedActiveProjects"
            options-label="name"
            options-value="project_id"
            :placeholder="$t('select-project-tooltip')"
            search
            search-key="name"
            @change="selectProject"
          />
        </div>
      </div>
      <div>
        <label class="text-label" for="keyword-domain">
          {{ $t('enter-url') }}
        </label>
        <div class="url-input-wrap">
          <div class="protocol-type">
            <span>{{ $t('protocol') }}</span>
          </div>
          <vue-simple-suggest
            ref="suggestComponent"
            v-model.trim="newKeyword.url"
            :filter-by-query="true"
            :list="getSuggestedUrls(apiKeywordTypes)"
            :min-length="0"
            :styles="autocompleteStyles"
            @input="() => urlInputHandler()"
          >
            <!-- can't use 'kw-text-field' component -->
            <input
              id="keyword-domain"
              v-model.trim="newKeyword.url"
              autocomplete="off"
              :class="keywordDomainClasses"
              placeholder="domain.com"
              type="text"
            />
            <div slot="suggestion-item" slot-scope="scope">
              <span
                class="suggestion-item-text"
                @mouseover="getTooltipForText(boldenSuggestion(scope, $refs), $event)"
                v-html="boldenSuggestion(scope, $refs)"
              />
            </div>
          </vue-simple-suggest>
          <div v-click-outside="handleDomainOutSideClick" class="domain-select-wrap">
            <button class="new-custom-dropdown-button" @click.stop="toggleDomainOpenedState" v-html="domainOption" />
            <add-keyword-selector
              v-if="domainOpened"
              class="add-keyword-selector_domain"
              :close-handler="closeDropMenu"
              :items="domainOptions"
              type="domain"
              :value.sync="domainOption"
              @item-click="setDropItem"
            />
          </div>
        </div>
      </div>
      <div class="controls">
        <kw-button
          class="show-serp-history"
          button-type="primary"
          :button-text="$t('save')"
          button-width="145px"
          :disabled="isDuplicateButtonDisabled"
          :loading="loading"
          @click="handleDuplicateKeywords"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { parseProject } from '@/store/utils';
import { autocompleteStyles, boldenSuggestion } from 'pages/add-keywords/utils';
import ValidationMixin from '@/mixins/ValidationMixin';
import AddKeywordsMixin from '@/mixins/AddKeywordsMixin';
import DropdownsMixin from '@/mixins/DropdownsMixin';
import VueSimpleSuggest from 'vue-simple-suggest';
import AddKeywordSelector from 'pages/add-keywords/components/AddKeywordSelector';
import { PROTOCOL_REGEXP_WITH_SLASHES } from '@/constants';
import ExactDomainModal from 'pages/add-keywords/components/ExactDomainModal';
import ToolTipRenderMixin from '@/mixins/ToolTipRenderMixin';
import { domainOptions } from './configs';

export default {
  name: 'DuplicateKeywordsModal',
  components: {
    'vue-simple-suggest': VueSimpleSuggest,
    'add-keyword-selector': AddKeywordSelector,
  },
  mixins: [AddKeywordsMixin, ValidationMixin, DropdownsMixin, ToolTipRenderMixin],
  data() {
    return {
      selectedProject: null,
      loading: false,
      autocompleteStyles,
      boldenSuggestion,
      domainOptions,
      apiKeywordTypes: ['se', 'sem'],
      newKeyword: {
        url: '',
        domain: false,
      },
      domainOpened: false,
      errorUrl: {},
      touched: false,
    };
  },
  computed: {
    ...mapGetters([
      'getOriginalActiveProjects',
      'getCurrentProject',
      'getSuggestedUrls',
      'getCategoriesForSelectedKeywords',
      'getSelectedKeywordsWithAllData',
      'getIsAllCheckboxesSelected',
      'getAllWithoutIdsData',
    ]),
    isSearch() {
      return this.$route.name === 'searchResults';
    },
    getParsedActiveProjects() {
      const parsedActiveProjects = [];
      this.getOriginalActiveProjects.forEach((originalProject) => {
        const project = parseProject(originalProject);
        const name = project.parent ? `${project.parent} / ${project.name}` : project.name;
        const hideProjectOnDropdown = this.getCategoriesForSelectedKeywords.find((cat) => cat === project.id);
        if (!hideProjectOnDropdown) {
          parsedActiveProjects.push({ ...project, name });
        }
      });
      return parsedActiveProjects;
    },
    keywordDomainClasses() {
      return {
        'domain-filed': true,
        'text-input': true,
        'text-input_invalid': this.errorUrl.text,
      };
    },
    customSelectClasses() {
      return { 'custom-dropdown_filled': this.selectedProject };
    },
    isDuplicateButtonDisabled() {
      return !this.selectedProject || (this.touched ? this.errorUrl.text : true);
    },
    domainOption() {
      return this.domainOptions.find((el) => el.value === this.newKeyword.domain).name;
    },
  },
  watch: {
    'newKeyword.url'() {
      !this.touched && (this.touched = true);
    },
  },
  mounted() {
    this.fetchSuggestedUrls();
  },
  methods: {
    ...mapActions(['resetAllKeywords', 'duplicateKeywordsAction', 'fetchSuggestedUrls']),
    selectProject(selected) {
      this.selectedProject = selected;
    },
    async handleDuplicateKeywords() {
      this.loading = true;
      let textError;
      try {
        const { project_id } = this.getCurrentProject || {};
        const params = {
          from_project_id: project_id || this.selectedProject,
          to_project_id: this.selectedProject,
          ids: [],
          without_ids: [],
          url: this.newKeyword.url,
        };
        if (this.getSelectedKeywordsWithAllData.length) {
          params.ids = this.getSelectedKeywordsWithAllData.map((item) => item.id);
        } else if (this.getIsAllCheckboxesSelected) {
          params.without_ids = this.getAllWithoutIdsData;
        }

        // exact/broad modal
        const urlWithoutProtocol = this.newKeyword.url.replace(PROTOCOL_REGEXP_WITH_SLASHES, '');
        const hasSlashes = urlWithoutProtocol.includes('/');

        if ((hasSlashes && !this.newKeyword.domain) || (!hasSlashes && this.newKeyword.domain)) {
          this.$modal.show(
            ExactDomainModal,
            {
              exact: this.newKeyword.domain,
              hasSlashes,
              url: urlWithoutProtocol,
              handler: async (exact) => {
                this.newKeyword.domain = exact;
                await this.duplicateKeywordsAction({ params, exact: this.newKeyword.domain }).catch((error) => {
                  const { response } = error;
                  textError = response?.data?.errors?.[0]?.detail;
                });
              },
            },
            {
              width: 350,
              height: 'auto',
              classes: 'v--modal',
            },
          );
        } else {
          await this.duplicateKeywordsAction({ params, exact: this.newKeyword.domain }).catch((error) => {
            const { response } = error;
            textError = response?.data?.errors?.[0]?.detail;
          });
        }

        this.resetAllKeywords();
      } catch (error) {
        console.log(error, 'error');
      }
      this.loading = false;
      if ('Domain does not exist, please check your spelling and try again.' !== textError) {
        this.close();
      }
    },
    close() {
      this.$emit('close');
    },
    urlInputHandler() {
      if (this.touched) {
        this.errorUrl = this.urlValidator(this.newKeyword.url);
      }
    },
    toggleDomainOpenedState() {
      this.domainOpened = !this.domainOpened;
    },
    setDropItem({ type, item }) {
      if (type === 'region' || type === 'language') {
        this.newKeyword[type] = item;
      } else {
        this.newKeyword[type] = item.value;
      }
      this.domainOpened = false;
    },
    handleDomainOutSideClick() {
      this.domainOpened && this.toggleDomainOpenedState();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~sass/partials/dropdowns';
.duplicate-keywords-modal {
  padding: 16px;
  position: relative;
  .duplicate-keywords-header {
    h4 {
      margin: 0;
      color: kw-color(kw-black, 7);
    }
    ::v-deep .button-closer {
      position: absolute;
      top: 8px;
      right: 8px;
    }
  }
  .duplicate-keywords-body {
    .duplicate-keywords-description {
      font-size: 12px;
      color: #24242c;
      margin-top: 19px;
    }
    .search-project-field {
      margin-top: 24px;
      max-width: 336px;
      .btn-group {
        width: 100%;
      }
      ::v-deep .form-control.dropdown-toggle {
        color: #828f9c;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      ::v-deep .custom-dropdown_filled {
        .form-control.dropdown-toggle {
          color: #24242c;
        }
      }
    }
    .text-label {
      font-weight: 500;
      font-size: 16px;
      margin-top: 16px;
      padding: 0;
    }
    .url-input-wrap {
      margin-top: 9px;
      display: flex;
      flex-wrap: row wrap;
      border-radius: 4px;
      max-width: 560px;

      .protocol-type {
        background-color: #f8f8f9;
        display: flex;
        align-items: center;
        border-bottom-left-radius: 4px;
        border-top-left-radius: 4px;
        border: 1px solid #dee5ec;
        box-shadow: 0px 2px 4px rgba(31, 63, 97, 0.1);
        justify-content: center;
        height: 45px;
        color: #828f9c;
        font-size: 13px;
        border-right: none;
        max-width: 75px;
        width: 100%;
      }
      .text-input {
        width: 100%;
        height: 45px;
        font-size: 13px;
        border: 1px solid #dee5ec;
        color: #24242c;
        border-radius: 0;

        &:disabled {
          border-radius: unset;
          border: unset !important;
        }
        &.text-input_invalid {
          border: 1px solid #cb1a02;
          color: #cb1a02;
        }
      }
      .suggestion-item-text {
        display: inline-block;
        width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .domain-select-wrap {
        min-width: 173px;
        position: relative;
        border: 1px solid #dee5ec;
        border-left: none;
        border-bottom-right-radius: 4px;
        border-top-right-radius: 4px;
        box-shadow: 0px 2px 4px rgba(31, 63, 97, 0.1);
        .new-custom-dropdown-button {
          border-bottom-left-radius: 0;
          border-top-left-radius: 0;
          width: 100%;
          height: 100%;
          padding-left: 10px;
          color: kw-color(kw-black, 1);
          li.active {
            color: kw-color(kw-blue, 1) !important;
          }
        }
        @media screen and (max-width: 959px) {
          min-width: 110px;
          z-index: 3;
        }
      }
    }
    .controls {
      display: inline-flex;
      button {
        margin-top: 24px;
      }
    }
  }
}
</style>
